import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

const LevyAccountDetailPage = () => {
  const { levyAccountId, responsiblePersonId } = useParams();
  const navigate = useNavigate();
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [levyDetails, setLevyDetails] = useState(null);
  const [showTenantDetailsModal, setShowTenantDetailsModal] = useState(false);
  const [pendingSplitChanges, setPendingSplitChanges] = useState([]);
  const [tenantDetails, setTenantDetails] = useState({
    first_name: "",
    last_name: "",
    email: "",
    contact_number: "",
  });
  const [tenantErrors, setTenantErrors] = useState({});

  // user access for dropdown
  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  // meter readings
  const [readingsLoading, setReadingsLoading] = useState(false);
  const [readingsError, setReadingsError] = useState("");
  const [unitReadings, setUnitReadings] = useState(null);
  const [selectedMeterType, setSelectedMeterType] = useState("all");

  // split account UI
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitError, setSplitError] = useState("");
  const [splitSuccess, setSplitSuccess] = useState("");
  const [unitBillings, setUnitBillings] = useState([]);
  const [chargeToDraft, setChargeToDraft] = useState({});
  const [savingSplit, setSavingSplit] = useState(false);

  // email popup + toast
  const [showEmailSentModal, setShowEmailSentModal] = useState(false);
  const [toastText, setToastText] = useState("");
  const [showToast, setShowToast] = useState(false);

  const [showP4PowerModal, setShowP4PowerModal] = useState(false);
  const [p4PowerLoading, setP4PowerLoading] = useState(false);
  const [p4PowerError, setP4PowerError] = useState("");
  const [p4PowerSuccess, setP4PowerSuccess] = useState("");
  const [p4PowerForm, setP4PowerForm] = useState({
    username: "",
    password: "",
  });
  const [p4PowerStep, setP4PowerStep] = useState("login");
  const [p4PowerMeters, setP4PowerMeters] = useState([]);
  const [selectedP4PowerMeterId, setSelectedP4PowerMeterId] = useState("");
  const [linkedP4PowerMeterId, setLinkedP4PowerMeterId] = useState("");
  const [p4PowerSelectionToken, setP4PowerSelectionToken] = useState("");

  const [p4PowerVerified, setP4PowerVerified] = useState(false);
  const [linkedP4PowerMeterNumber, setLinkedP4PowerMeterNumber] = useState("");
  const [p4PowerLastConsumption, setP4PowerLastConsumption] = useState("");

  const API_BASE = "https://portal.gmacc.co.za/api";
  //const API_BASE = "http://127.0.0.1:8000/api";

  const formatRand = (amount) => {
    if (amount === undefined || amount === null || amount === "") return "0,00";
    const n = Number(amount);
    if (Number.isNaN(n)) return String(amount);
    const [intPart, decPart] = n.toFixed(2).split(".");
    const withSpaces = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
    return `${withSpaces},${decPart}`;
  };

  const getJwtPayload = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return {};
      return JSON.parse(atob(token.split(".")[1] || ""));
    } catch {
      return {};
    }
  };

  const getUserEmail = () => {
    const fromToken = getJwtPayload()?.email;
    return fromToken || localStorage.getItem("userEmail") || "";
  };

  // load levy
  useEffect(() => {
    document.title = "GMA Portal || Levy Account Details View";
    const fetchLevyDetails = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }
      try {
        const response = await fetch(
          `${API_BASE}/levy-account/${levyAccountId}/${responsiblePersonId}/`,
          {
            method: "GET",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        const data = await response.json();
        if (response.ok) {
          setLevyDetails(data);
        } else {
          setError(data.error || "Failed to fetch levy account details.");
        }
      } catch (err) {
        setError("Something went wrong.");
      } finally {
        setLoading(false);
      }
    };
    fetchLevyDetails();
  }, [levyAccountId, responsiblePersonId, navigate]);

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);

      // Only force closed when moving to smaller screens.
      // On desktop, keep whatever state the user last chose.
      if (!desktop) {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    function handleClick(e) {
      // On desktop/laptop, only the burger should control the sidebar
      if (isDesktop) return;

      const sidebar = document.getElementsByClassName(styles.sidebar)[0];
      const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

      if (!sidebar || !hamburger) return;

      const clickedInsideSidebar = sidebar.contains(e.target);
      const clickedHamburger = hamburger.contains(e.target);

      if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
        setSidebarOpen(false);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
              ? name
              : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(`${API_BASE}/contact/determine-access/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ email, contact_id }),
        });

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  // load meter readings after levy arrives
  useEffect(() => {
    const fetchMeterReadings = async (unitId) => {
      setReadingsLoading(true);
      setReadingsError("");
      setUnitReadings(null);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          navigate("/");
          return;
        }
        const res = await fetch(`${API_BASE}/units/${unitId}/meter-readings/`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok)
          throw new Error(data?.error || "Failed to fetch meter readings");
        setUnitReadings(data);
        setSelectedMeterType("all");
      } catch (err) {
        setReadingsError(err.message || "Failed to fetch meter readings");
      } finally {
        setReadingsLoading(false);
      }
    };

    if (levyDetails && levyDetails._cj_unitname_value) {
      fetchMeterReadings(levyDetails._cj_unitname_value);
    }
  }, [levyDetails, navigate, API_BASE]);

  // build meter data
  const allMeters = useMemo(() => {
    if (!unitReadings?.unit_billings?.length) return [];
    const meters = [];
    for (const ub of unitReadings.unit_billings) {
      if (!ub?.meters?.length) continue;
      for (const m of ub.meters) {
        meters.push({
          unit_billing_id: ub.unit_billing_id,
          unit_billing_name: ub.name,
          charge_to_label:
            (typeof ub?.[
              "cj_chargeto@OData.Community.Display.V1.FormattedValue"
            ] === "string"
              ? ub["cj_chargeto@OData.Community.Display.V1.FormattedValue"]
              : ub?.cj_chargeto_formatted ||
                ub?.["cj_chargeto_formatted"] ||
                "") ||
            (String(ub?.cj_chargeto) === "120810001" ||
            String(ub?.cj_chargeto) === "1"
              ? "Tenant"
              : "Owner"),
          ...m,
        });
      }
    }
    return meters;
  }, [unitReadings]);

  const meterTypeOptions = useMemo(() => {
    const set = new Set();
    for (const m of allMeters) set.add(m.meter_type || "Unknown");
    return ["all", ...Array.from(set)];
  }, [allMeters]);

  const filteredMeters = useMemo(() => {
    if (selectedMeterType === "all") return allMeters;
    return allMeters.filter(
      (m) => String(m.meter_type || "Unknown") === String(selectedMeterType),
    );
  }, [allMeters, selectedMeterType]);

  const handleChangeFriendlyLetter = () => {
    if (levyDetails) {
      navigate("/fr-change", {
        state: {
          levyAccountNumber: levyDetails.cj_sageaccountnumber,
          friendlyReminderActive: levyDetails.cj_sendfriendlyreminder,
          levyAccountId: levyDetails.cj_gmaaccountnumberid,
        },
      });
    }
  };

  const handleTrusteePortalClick = () => navigate("/trustee-portal");
  const handleContractorPortalClick = () => navigate("/contractor-portal");

  // split helpers
  const normalizeChargeTo = (ub) => {
    const formatted =
      ub?.["cj_chargeto@OData.Community.Display.V1.FormattedValue"] ||
      ub?.cj_chargeto_formatted ||
      ub?.["cj_chargeto_formatted"];
    if (
      typeof formatted === "string" &&
      formatted.toLowerCase().includes("tenant")
    )
      return "tenant";
    if (
      typeof formatted === "string" &&
      formatted.toLowerCase().includes("owner")
    )
      return "owner";
    const code = ub?.cj_chargeto;
    if (code !== undefined && code !== null) {
      const s = String(code);
      if (s === "120810001" || s === "1") return "tenant";
      return "owner";
    }
    return "owner";
  };

  const openDisclaimer = () => {
    setSplitError("");
    setSplitSuccess("");
    setShowDisclaimer(true);
  };

  const handleDisclaimerAccept = async () => {
    setShowDisclaimer(false);
    await openSplitModal();
  };

  const openSplitModal = async () => {
    try {
      if (!levyDetails?._cj_unitname_value) return;

      setSplitLoading(true);
      setSplitError("");
      setSplitSuccess("");

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      const params = new URLSearchParams();

      if (hasLinkedP4PowerMeter) {
        params.set("p4power_smart_meter", "true");
      }

      const queryString = params.toString();

      const res = await fetch(
        `${API_BASE}/units/${levyDetails._cj_unitname_value}/billings/${
          queryString ? `?${queryString}` : ""
        }`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.error || "Failed to load unit billings");
      }

      const list = Array.isArray(data?.value) ? data.value : [];

      const filteredList = hasLinkedP4PowerMeter
        ? list
        : list.filter((ub) => {
            const desc = (ub.cj_statementdescription || "").toLowerCase();

            return (
              !desc.includes("monthly") &&
              !desc.includes("csos") &&
              !desc.includes("eua") &&
              !desc.includes("reserve")
            );
          });

      setUnitBillings(filteredList);

      const draft = {};

      for (const ub of filteredList) {
        draft[ub.cj_unitbillingid] = normalizeChargeTo(ub);
      }

      setChargeToDraft(draft);
      setTenantDetails({
        first_name: "",
        last_name: "",
        email: "",
        contact_number: "",
      });
      setTenantErrors({});
      setPendingSplitChanges([]);
      setShowSplitModal(true);
    } catch (e) {
      setSplitError(e.message || "Failed to open Split Accounts");
    } finally {
      setSplitLoading(false);
    }
  };

  const updateDraft = (id, val) => {
    setChargeToDraft((prev) => ({ ...prev, [id]: val }));
  };
  const resetTenantDetailsModal = () => {
    setTenantDetails({
      first_name: "",
      last_name: "",
      email: "",
      contact_number: "",
    });
    setTenantErrors({});
    setPendingSplitChanges([]);
    setShowTenantDetailsModal(false);
  };

  const handleTenantDetailChange = (field, value) => {
    setTenantDetails((prev) => ({
      ...prev,
      [field]: value,
    }));

    setTenantErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  const validateTenantDetails = () => {
    const errors = {};

    const firstName = tenantDetails.first_name.trim();
    const lastName = tenantDetails.last_name.trim();
    const email = tenantDetails.email.trim();
    const contactNumber = tenantDetails.contact_number.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^(?:\+27|0)[6-8][0-9]{8}$/;

    if (!firstName) {
      errors.first_name = "First name is required.";
    }

    if (!lastName) {
      errors.last_name = "Last name is required.";
    }

    if (!email) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(email)) {
      errors.email = "Enter a valid email address.";
    }

    if (!contactNumber) {
      errors.contact_number = "Contact number is required.";
    } else {
      const cleaned = contactNumber.replace(/\s+/g, "");
      if (!mobileRegex.test(cleaned)) {
        errors.contact_number =
          "Enter a valid mobile number, for example 0821234567 or +27821234567.";
      }
    }

    setTenantErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateP4PowerLastConsumption = (value) => {
    const trimmed = String(value).trim();
    if (!trimmed) {
      return "Last consumption charged is required.";
    }
    const num = Number(trimmed);
    if (Number.isNaN(num)) {
      return "Enter a valid decimal number.";
    }
    if (num <= 0) {
      return "Value must be greater than 0.00.";
    }
    return ""; // no error
  };

  const buildSplitChanges = () => {
    const changes = [];

    for (const ub of unitBillings) {
      const id = ub.cj_unitbillingid;
      const currentNorm = normalizeChargeTo(ub);
      const draftVal = (chargeToDraft[id] || "owner").toLowerCase();

      if (draftVal !== currentNorm) {
        changes.push({
          unit_billing_id: id,
          new_charge_to: draftVal,
        });
      }
    }

    return changes;
  };

  const submitSplitApprovalRequest = async (changes, tenantPayload = null) => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    const emailFromToken = getUserEmail();
    if (!emailFromToken) throw new Error("No user email found on session.");

    const payload = {
      unit_id: levyDetails._cj_unitname_value,
      changes,
      email: emailFromToken,
      tenant_details: tenantPayload,
      p4power:
  isP4PowerSmartMeteringEnabled &&
  p4PowerVerified &&
  linkedP4PowerMeterId &&
  linkedP4PowerMeterNumber &&
  p4PowerSelectionToken &&
  changes.some((change) => change.new_charge_to === "tenant")
    ? {
        enabled: true,
        levy_account_id: levyDetails.cj_gmaaccountnumberid,
        meter_id: linkedP4PowerMeterId,
        meter_number: linkedP4PowerMeterNumber,
        selection_token: p4PowerSelectionToken,
        last_consumption_charged: Number(p4PowerLastConsumption), // ← added
      }
    : null,
    };

    const res = await fetch(
      `${API_BASE}/unit-billings/split-changes/request/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      },
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data?.error || "Failed to request approval email");
    }

    return { data, emailFromToken };
  };

  const handleTenantDetailsSubmit = async () => {
    try {
      if (!validateTenantDetails()) return;

      setSplitError("");
      setSplitSuccess("");
      setSavingSplit(true);

      const tenantPayload = {
        first_name: tenantDetails.first_name.trim(),
        last_name: tenantDetails.last_name.trim(),
        email: tenantDetails.email.trim(),
        contact_number: tenantDetails.contact_number.trim().replace(/\s+/g, ""),
      };

      const { emailFromToken } = await submitSplitApprovalRequest(
        pendingSplitChanges,
        tenantPayload,
      );

      resetTenantDetailsModal();
      setShowSplitModal(false);

      const msg = `Tenant details were captured, the tenant account was updated, and an approval email has been sent to ${emailFromToken}. Please check your inbox to Approve or Decline.`;
      setSplitSuccess(msg);
      setToastText(msg);
      setShowToast(true);
      setShowEmailSentModal(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (e) {
      setSplitError(e.message || "Failed to save split changes");
    } finally {
      setSavingSplit(false);
    }
  };

  const handleSaveSplitChanges = async () => {
    try {
      setSplitError("");
      setSplitSuccess("");
      setSavingSplit(true);

      const changes = buildSplitChanges();

      if (changes.length === 0) {
        setSplitSuccess("No changes detected.");
        return;
      }

      const hasTenantChanges = changes.some(
        (change) => change.new_charge_to === "tenant",
      );

      if (hasTenantChanges) {
        setPendingSplitChanges(changes);
        setSavingSplit(false);
        setShowTenantDetailsModal(true);
        return;
      }

      const { emailFromToken } = await submitSplitApprovalRequest(
        changes,
        null,
      );

      setShowSplitModal(false);

      const msg = `The split account approval email has been sent to ${emailFromToken}. You have 30 minutes to respond before it expires. Please check your inbox to Approve or Decline.`;
      setSplitSuccess(msg);
      setToastText(msg);
      setShowToast(true);
      setShowEmailSentModal(true);
      setTimeout(() => setShowToast(false), 4000);
    } catch (e) {
      setSplitError(e.message || "Failed to save split changes");
    } finally {
      setSavingSplit(false);
    }
  };
  const handleLandlordPortalClick = () => {
    localStorage.setItem("rental_portal_mode", "landlord");
    navigate("/rental-portal");
  };

  const handleTenantPortalClick = () => {
    localStorage.setItem("rental_portal_mode", "tenant");
    navigate("/rental-portal");
  };

  //P4power Intergration

  const isP4PowerSmartMeteringEnabled = Boolean(
    levyDetails?.cj_LinkedBuilding?.cj_p4powersmartmeteringenabled ||
    levyDetails?.cj_p4powersmartmeteringenabled,
  );

  const currentP4PowerMeterNumber =
    linkedP4PowerMeterNumber || levyDetails?.cr446_p4powermeternumber || "";

  const hasLinkedP4PowerMeter = Boolean(
    isP4PowerSmartMeteringEnabled && currentP4PowerMeterNumber,
  );

  const handleLinkP4PowerMeter = () => {
    if (hasLinkedP4PowerMeter) return;

    setP4PowerError("");
    setP4PowerSuccess("");
    setP4PowerStep("login");
    setP4PowerMeters([]);
    setSelectedP4PowerMeterId("");
    setP4PowerLastConsumption(""); // ← added
    setP4PowerForm({
      username: "",
      password: "",
    });
    setShowP4PowerModal(true);
  };

  const handleP4PowerLoginSubmit = async () => {
    try {
      setP4PowerError("");
      setP4PowerSuccess("");

      const username = p4PowerForm.username.trim();
      const password = p4PowerForm.password;

      if (!username || !password) {
        setP4PowerError("P4Power email and password are required.");
        return;
      }

      setP4PowerLoading(true);

      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      const res = await fetch(`${API_BASE}/p4power/login/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          username,
          password,
          levy_account_id: levyDetails.cj_gmaaccountnumberid,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "P4Power login failed.");
      }

      const meters = Array.isArray(data?.meters) ? data.meters : [];

      if (meters.length === 0) {
        setP4PowerMeters([]);
        setP4PowerError("No available P4Power meters are linked to this user.");
        return;
      }

      const currentMeter = meters.find(
        (meter) => String(meter.number) === String(currentP4PowerMeterNumber),
      );

      setP4PowerMeters(meters);
      setSelectedP4PowerMeterId(
        currentMeter?.id || (meters.length === 1 ? meters[0].id : ""),
      );
      setP4PowerStep("select");
      setP4PowerSuccess(
        "Login successful. Select the meter that must be linked.",
      );
    } catch (err) {
      setP4PowerError(err.message || "P4Power login failed.");
    } finally {
      setP4PowerLoading(false);

      // Do not retain the P4Power password after authentication.
      setP4PowerForm((prev) => ({
        ...prev,
        password: "",
      }));
    }
  };

  const handleP4PowerMeterSubmit = () => {
  const selectedMeter = p4PowerMeters.find(
    (meter) => meter.id === selectedP4PowerMeterId,
  );

  if (!selectedMeter) {
    setP4PowerError("Select a P4Power meter.");
    return;
  }

  if (!selectedMeter.selectionToken) {
    setP4PowerError("The selected meter could not be verified. Log in again.");
    return;
  }

  const consumptionError = validateP4PowerLastConsumption(p4PowerLastConsumption);
  if (consumptionError) {
    setP4PowerError(consumptionError);
    return;
  }
  // ────────────────────

  setP4PowerVerified(true);
  setLinkedP4PowerMeterId(selectedMeter.id);
  setLinkedP4PowerMeterNumber(selectedMeter.number);
  setP4PowerSelectionToken(selectedMeter.selectionToken);
  setP4PowerSuccess("P4Power meter selected. You can now split the levy.");
  setShowP4PowerModal(false);
};

  return (
    <div className={styles.page}>
      {/* top bar – light like screenshot 2 */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            Levy Details{" "}
            {levyDetails?.cj_sageaccountnumber
              ? ` - ${levyDetails.cj_sageaccountnumber}`
              : ""}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      {/* main 2-column layout: sidebar + content */}
      <div className={styles.shell}>
        {/* left sidebar (static like screenshot) */}
        <PortalSidebar
          activePortal="owner"
          userAccess={userAccess}
          loadingAccess={loadingAccess}
          sidebarOpen={sidebarOpen}
          ticketRole="owner"
        />

        {/* right side content */}
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {loading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            ) : error ? (
              <p className={styles.errorText}>{error}</p>
            ) : levyDetails ? (
              <>
                {/* top ageing summary like screenshot 2 */}
                <div className={styles.ageSummaryCard}>
                  <div className={styles.ageMainRow}>
                    <div className={styles.ageMetric}>
                      <span className={styles.ageLabel}>Balance:</span>
                      <span className={styles.ageValue}>
                        R {formatRand(levyDetails.cj_agetotal)}
                      </span>
                    </div>
                    <div className={styles.ageMetric}>
                      <span className={styles.ageLabel}>Current:</span>
                      <span className={styles.ageValue}>
                        R {formatRand(levyDetails.cj_agecurrent)}
                      </span>
                    </div>
                    <div className={styles.ageMetric}>
                      <span className={styles.ageLabel}>30 Days:</span>
                      <span className={styles.ageValue}>
                        R {formatRand(levyDetails.cj_age30days)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.ageSecondaryRow}>
                    <div className={styles.agePill}>
                      <span>60 Days:</span>
                      <p>R {formatRand(levyDetails.cj_age60days)}</p>
                    </div>
                    <div className={styles.agePill}>
                      <span>90 Days:</span>
                      <p>R {formatRand(levyDetails.cj_age90days)}</p>
                    </div>
                    <div className={styles.agePill}>
                      <span>120 Days:</span>
                      <p>R {formatRand(levyDetails.cj_age120days)}</p>
                    </div>
                    <div className={styles.agePill}>
                      <span>150 Days:</span>
                      <p>R {formatRand(levyDetails.cj_age150days)}</p>
                    </div>
                    <div className={styles.agePill}>
                      <span>180 Days:</span>
                      <p>R {formatRand(levyDetails.cj_age180days)}</p>
                    </div>
                  </div>
                </div>

                {/* two cards below: statement + info */}
                <div className={styles.detailGrid}>
                  <div className={styles.statementCard}>
                    <h2 className={styles.cardTitle}>STATEMENT ADDRESS:</h2>
                    <div className={styles.statementRow}>
                      <span>Owner</span>
                      <span>
                        {levyDetails.cj_responsablepartyfullname || "-"}
                      </span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Billing Address 1</span>
                      <span>{levyDetails.cj_doornumber || "-"}</span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Billing Address 2</span>
                      <span>{levyDetails.cj_billingaddress1 || "-"}</span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Billing Address 3</span>
                      <span>{levyDetails.cj_billingaddress2 || "-"}</span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Billing Address 4</span>
                      <span>{levyDetails.cj_billingaddress3 || "-"}</span>
                    </div>
                    <p className={styles.statementNote}>
                      To amend the billing details, go to the{" "}
                      <Link to="/owner-portal" className={styles.link}>
                        Accounts Page
                      </Link>{" "}
                      and click on the Modify button.
                    </p>
                  </div>

                  <div className={styles.infoCard}>
                    <h2 className={styles.cardTitle}>INFORMATION:</h2>
                    <div className={styles.statementRow}>
                      <span>Handed Over to Legal</span>
                      <span>{levyDetails.cj_legal ? "Yes" : "No"}</span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Active Arrangement</span>
                      <span>{levyDetails.cj_activeaod ? "Yes" : "No"}</span>
                    </div>
                    <div className={styles.statementRow}>
                      <span>Friendly Letter Active</span>
                      <span>
                        {levyDetails.cj_sendfriendlyreminder ? "Yes" : "No"}
                      </span>
                    </div>

                    <div className={styles.infoButtonBar}>
                      <button
                        className={styles.buildingViewBtn}
                        onClick={handleChangeFriendlyLetter}
                      >
                        Change Friendly Letter
                      </button>
                      <button
                        className={styles.buildingViewBtn}
                        onClick={() =>
                          navigate("/download-statement", {
                            state: {
                              sageAccountNumber:
                                levyDetails.cj_sageaccountnumber,
                              gmaAccountNumberID:
                                levyDetails.cj_gmaaccountnumberid,
                              userEmail: localStorage.getItem("userEmail"),
                              userProfileID:
                                localStorage.getItem("userProfileId"),
                            },
                          })
                        }
                      >
                        Download Statement
                      </button>
                      <button
                        className={styles.buildingViewBtn}
                        onClick={openDisclaimer}
                        title="Manage how charges are split between Owner and Tenant"
                      >
                        {hasLinkedP4PowerMeter
                          ? "Split Levy's To Smart Meter"
                          : "Manage Split Accounts"}
                      </button>
                      {isP4PowerSmartMeteringEnabled && (
                        <div className={styles.p4PowerLinkedMeterBlock}>
                          {hasLinkedP4PowerMeter ? (
                            <span className={styles.p4PowerLinkedMeterText}>
                              Linked Meter: {currentP4PowerMeterNumber}
                            </span>
                          ) : (
                            <button
                              className={styles.buildingViewBtn}
                              onClick={handleLinkP4PowerMeter}
                              title="Link this levy account to a P4Power smart meter"
                            >
                              Link P4Power Smart Meter
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {splitSuccess && (
                      <div className={styles.successText}>{splitSuccess}</div>
                    )}
                    {splitError && (
                      <div className={styles.errorText}>{splitError}</div>
                    )}
                  </div>
                </div>

                {/* meter readings bottom card */}
                <div className={styles.readingsCard}>
                  <div className={styles.readingsHeader}>
                    <h2 className={styles.cardTitle}>LATEST METER READINGS</h2>
                    <div className={styles.readingsControls}>
                      <label className={styles.readingsLabel}>
                        Meter Type:
                      </label>
                      <select
                        className={styles.readingsSelect}
                        value={selectedMeterType}
                        onChange={(e) => setSelectedMeterType(e.target.value)}
                      >
                        {meterTypeOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === "all" ? "All" : opt}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {readingsLoading && (
                    <div className={styles.readingsLoadingBar} />
                  )}

                  {readingsError ? (
                    <p className={styles.errorText}>{readingsError}</p>
                  ) : !unitReadings ? (
                    <p className={styles.infoText}>No readings to display.</p>
                  ) : filteredMeters.length === 0 ? (
                    <p className={styles.infoText}>
                      No meters found for this filter.
                    </p>
                  ) : (
                    <div className={styles.metersList}>
                      {filteredMeters.map((m) => (
                        <div key={m.meter_id} className={styles.meterCard}>
                          <div className={styles.meterCardHeader}>
                            <div className={styles.meterTitle}>
                              <span className={styles.meterName}>
                                {m.meter_name || "Meter"}
                              </span>
                              <span className={styles.meterTypeBadge}>
                                {m.meter_type || "Unknown"}
                              </span>
                            </div>
                            {m.unit_billing_name && (
                              <div className={styles.meterBillingHint}>
                                Billing: {m.unit_billing_name}
                              </div>
                            )}
                          </div>

                          <div className={styles.tableWrap}>
                            <table className={styles.readingsTable}>
                              <thead>
                                <tr>
                                  <th>Current Date</th>
                                  <th>Previous Date</th>
                                  <th>Consumption</th>
                                  <th>Days</th>
                                  <th>Charge To</th>
                                  <th>Charge (R)</th>
                                  <th>Charge Breakdown</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(m.readings || []).map((r, idx) => (
                                  <tr key={idx}>
                                    <td>
                                      {r.current_reading_date
                                        ? new Date(
                                            r.current_reading_date,
                                          ).toLocaleDateString()
                                        : "-"}
                                    </td>
                                    <td>
                                      {r.previous_reading_date
                                        ? new Date(
                                            r.previous_reading_date,
                                          ).toLocaleDateString()
                                        : "-"}
                                    </td>
                                    <td>{r.consumption ?? "-"}</td>
                                    <td>{r.days ?? "-"}</td>
                                    <td>{m.charge_to_label || "-"}</td>
                                    <td>
                                      {r.charge !== undefined &&
                                      r.charge !== null
                                        ? `R ${formatRand(r.charge)}`
                                        : "-"}
                                    </td>
                                    <td>
                                      <pre className={styles.chargeBreakdown}>
                                        {r.charge_breakdown
                                          ? r.charge_breakdown
                                          : "-"}
                                      </pre>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {showP4PowerModal && (
                  <div className={styles.modalOverlay}>
                    <div
                      className={styles.modal}
                      role="dialog"
                      aria-modal="true"
                      aria-labelledby="p4power-modal-title"
                    >
                      <div className={styles.modalHeaderRow}>
                        <h2
                          id="p4power-modal-title"
                          className={styles.modalTitle}
                        >
                          Link P4Power Smart Meter
                        </h2>
                        <button
                          className={styles.modalClose}
                          disabled={p4PowerLoading}
                          onClick={() => setShowP4PowerModal(false)}
                        >
                          ✕
                        </button>
                      </div>

                      <div className={styles.modalBody}>
                        {hasLinkedP4PowerMeter ? (
                          <>
                            <p>
                              This unit is connected to P4Power smart meter{" "}
                              <strong>{currentP4PowerMeterNumber}</strong>.
                            </p>

                            <p>
                              You are about to update which eligible charges are
                              allocated to this unit’s P4Power smart meter
                              account. Your selection determines whether future
                              eligible charges are allocated to the Owner or
                              Tenant.
                            </p>

                            <p>
                              By selecting <strong>Tenant</strong>, you
                              acknowledge that the selected charges will be
                              billed to the linked P4Power smart meter. These
                              charges will then be recovered through the P4Power
                              wallet system. The resident assigned to the wallet
                              will be able to fund it and keep it in credit.
                              However, the{" "}
                              <strong>
                                Owner remains ultimately responsible for the
                                account
                              </strong>{" "}
                              and for ensuring that all amounts due are settled.
                            </p>

                            <p>
                              By selecting <strong>Owner</strong>, the selected
                              charges will remain billed directly to the Owner
                              through the Owner’s levy statement and will not be
                              allocated to the P4Power wallet account.
                            </p>

                            <p>
                              Please ensure that you have authority to make this
                              change. An approval email will be sent, and{" "}
                              <strong>
                                no billing changes will be made unless approved
                              </strong>{" "}
                              through that email.
                            </p>

                            <p>
                              <strong>Credit Control Notice:</strong>
                              <br />
                              The{" "}
                              <strong>
                                Owner remains responsible for settling all
                                outstanding amounts
                              </strong>{" "}
                              if the resident or Tenant does not settle them.
                            </p>

                            <p>
                              Additional charges may apply. Please refer to the
                              P4Power terms and conditions.
                            </p>

                            <p>
                              <strong>
                                Certain charges are not eligible to be moved to
                                the P4Power wallet account. Those charges will
                                not be available to change.
                              </strong>
                            </p>

                            <p>
                              This request will trigger an approval email. No
                              changes will apply until it has been approved.
                            </p>
                          </>
                        ) : p4PowerStep === "login" ? (
                          <>
                            <p className={styles.infoText}>
                              Log in to P4Power to load the meters available to
                              your profile.
                            </p>

                            <div className={styles.inputGroup}>
                              <label
                                htmlFor="p4power-username"
                                className={styles.inputLabel}
                              >
                                P4Power Email
                              </label>

                              <input
                                id="p4power-username"
                                type="email"
                                autoComplete="username"
                                className={styles.inputFieldLight}
                                value={p4PowerForm.username}
                                onChange={(e) =>
                                  setP4PowerForm((prev) => ({
                                    ...prev,
                                    username: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div className={styles.inputGroup}>
                              <label
                                htmlFor="p4power-password"
                                className={styles.inputLabel}
                              >
                                P4Power Password
                              </label>

                              <input
                                id="p4power-password"
                                type="password"
                                autoComplete="current-password"
                                className={styles.inputFieldLight}
                                value={p4PowerForm.password}
                                onChange={(e) =>
                                  setP4PowerForm((prev) => ({
                                    ...prev,
                                    password: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <p className={styles.infoText}>
                              Select the P4Power meter that must be linked to
                              this levy account.
                            </p>

                            <div className={styles.inputGroup}>
                              <label
                                htmlFor="p4power-meter"
                                className={styles.inputLabel}
                              >
                                Available Meter
                              </label>

                              <select
                                id="p4power-meter"
                                className={styles.selectPill}
                                value={selectedP4PowerMeterId}
                                onChange={(e) =>
                                  setSelectedP4PowerMeterId(e.target.value)
                                }
                              >
                                <option value="">Select a meter</option>

                                {p4PowerMeters.map((meter) => (
                                  <option key={meter.id} value={meter.id}>
                                    {meter.building || "Building unavailable"} —{" "}
                                    {meter.unit || "Unit unavailable"} — Meter{" "}
                                    {meter.number}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className={styles.inputGroup}>
                              <label
                                htmlFor="p4power-last-consumption"
                                className={styles.inputLabel}
                              >
                                Last Consumption Charged
                              </label>

                              <input
                                id="p4power-last-consumption"
                                type="number"
                                step="0.01"
                                min="0.01"
                                placeholder="e.g. 12.50"
                                className={styles.inputFieldLight}
                                value={p4PowerLastConsumption}
                                onChange={(e) =>
                                  setP4PowerLastConsumption(e.target.value)
                                }
                              />
                            </div>
                          </>
                        )}

                        {p4PowerError && (
                          <div className={styles.errorText}>{p4PowerError}</div>
                        )}

                        {p4PowerSuccess && (
                          <div className={styles.successText}>
                            {p4PowerSuccess}
                          </div>
                        )}
                      </div>

                      <div className={styles.modalActions}>
                        {p4PowerStep === "login" ? (
                          <button
                            className={styles.modalBtnPrimary}
                            disabled={p4PowerLoading}
                            onClick={handleP4PowerLoginSubmit}
                          >
                            {p4PowerLoading
                              ? "Logging in..."
                              : "Login to P4Power"}
                          </button>
                        ) : (
                          <>
                            <button
                              className={styles.modalBtnPrimary}
                              disabled={
                                p4PowerLoading || !selectedP4PowerMeterId
                              }
                              onClick={handleP4PowerMeterSubmit}
                            >
                              {p4PowerLoading
                                ? "Linking..."
                                : "Link Selected Meter"}
                            </button>

                            <button
                              className={styles.modalBtnSecondary}
                              disabled={p4PowerLoading}
                              onClick={() => {
                                setP4PowerStep("login");
                                setP4PowerMeters([]);
                                setSelectedP4PowerMeterId("");
                                setP4PowerError("");
                                setP4PowerSuccess("");
                              }}
                            >
                              Use another login
                            </button>
                          </>
                        )}

                        <button
                          className={styles.modalBtnSecondary}
                          disabled={p4PowerLoading}
                          onClick={() => setShowP4PowerModal(false)}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Modals + toast */}
                {showDisclaimer && (
                  <div
                    className={styles.modalOverlay}
                    onClick={() => setShowDisclaimer(false)}
                  >
                    <div
                      className={styles.modal}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2 className={styles.modalTitle}>
                        Important: Allocation of Financial Responsibility
                      </h2>
                      <div className={styles.modalBody}>
                        {hasLinkedP4PowerMeter ? (
                          <>
                            <p>
                              This unit is connected to P4Power smart meter{" "}
                              <strong>{currentP4PowerMeterNumber}</strong>.
                            </p>

                            <p>
                              You are about to update which eligible fixed
                              charges are allocated through this unit’s P4Power
                              smart meter account. Your selection determines
                              whether future eligible charges are allocated to
                              the Owner or Tenant.
                            </p>

                            <p>
                              By selecting <strong>Tenant</strong>, you
                              acknowledge that the Tenant will receive the
                              selected charges through P4Power and may be
                              contacted regarding payment. However, the{" "}
                              <strong>
                                Owner remains ultimately responsible for the
                                account
                              </strong>{" "}
                              and for ensuring that all amounts due are settled.
                            </p>

                            <p>
                              By selecting <strong>Owner</strong>, the selected
                              charges will remain billed directly to the Owner
                              and will not be allocated to the Tenant’s P4Power
                              account.
                            </p>

                            <p>
                              Please ensure that you have authority to make this
                              change. An approval email will be sent, and{" "}
                              <strong>
                                no billing changes will apply unless approved
                              </strong>{" "}
                              through that email.
                            </p>

                            <p>
                              <strong>Credit Control Notice:</strong>
                              <br />
                              If the Tenant account reaches{" "}
                              <strong>60 days in arrears</strong>, the split
                              account will be closed and the charges will revert
                              to the Owner’s account. The{" "}
                              <strong>
                                Owner remains responsible for settling all
                                outstanding amounts
                              </strong>
                              .
                            </p>

                            <p>
                              Additional charges, including sundry or
                              P4Power-related charges, may apply to charges
                              allocated to the Tenant.
                            </p>

                            <p>
                              <strong>
                                Monthly levies, CSOS fees and other ineligible
                                billing categories cannot be allocated to the
                                Tenant through P4Power.
                              </strong>
                            </p>

                            <p>
                              This request will trigger an approval email. No
                              changes will apply until it has been approved.
                            </p>
                          </>
                        ) : (
                          <>
                            <p>
                              You are about to update how this unit’s charges
                              are allocated between the Owner and the Tenant.
                              Your selection determines who will receive the
                              charges on future billing.
                            </p>

                            <p>
                              By selecting <strong>Tenant</strong>, you
                              acknowledge that the Tenant will receive the
                              selected charges and may be contacted regarding
                              payment. However, the{" "}
                              <strong>
                                Owner remains ultimately responsible for the
                                account
                              </strong>{" "}
                              and for ensuring that all amounts due are settled.
                            </p>

                            <p>
                              By selecting <strong>Owner</strong>, all selected
                              charges will remain billed directly to the Owner.
                            </p>

                            <p>
                              Please ensure you have the authority to make this
                              change. An email confirmation will be sent for
                              approval, and{" "}
                              <strong>
                                no changes will apply unless approved
                              </strong>{" "}
                              via that email.
                            </p>

                            <p>
                              <strong>Credit Control Notice:</strong>
                              <br />
                              If the account reaches{" "}
                              <strong>60 days in arrears</strong>, it will be
                              closed and reverted back to the Owner’s account.
                              The{" "}
                              <strong>
                                Owner remains responsible for settling all
                                outstanding amounts
                              </strong>
                              .
                            </p>

                            <p>
                              For split accounts allocated to Tenants,
                              additional charges, such as sundry charges, may
                              apply.
                            </p>

                            <p>
                              <strong>
                                Monthly levies and CSOS fees cannot be allocated
                                to Tenants.
                              </strong>
                            </p>

                            <p>
                              This will trigger an approval email. No changes
                              apply until it is approved.
                            </p>
                          </>
                        )}
                      </div>
                      <div className={styles.modalActions}>
                        <button
                          className={styles.modalBtnPrimary}
                          onClick={handleDisclaimerAccept}
                        >
                          I Accept
                        </button>
                        <button
                          className={styles.modalBtnSecondary}
                          onClick={() => setShowDisclaimer(false)}
                        >
                          I Decline
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showSplitModal && (
                  <div
                    className={styles.modalOverlay}
                    onClick={() => setShowSplitModal(false)}
                  >
                    <div
                      className={styles.modalWide}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={styles.modalHeaderRow}>
                        <h2 className={styles.modalTitle}>
                          {hasLinkedP4PowerMeter
                            ? "Split Levy's To Smart Meter"
                            : "Manage Split Accounts"}
                        </h2>
                        <button
                          className={styles.modalClose}
                          onClick={() => setShowSplitModal(false)}
                        >
                          ✕
                        </button>
                      </div>

                      {splitLoading ? (
                        <div className={styles.readingsLoadingBar} />
                      ) : (
                        <>
                          {splitError && (
                            <div className={styles.errorText}>{splitError}</div>
                          )}
                          {splitSuccess && (
                            <div className={styles.successText}>
                              {splitSuccess}
                            </div>
                          )}

                          {unitBillings.length === 0 ? (
                            <p className={styles.infoText}>
                              No unit billings found for this unit.
                            </p>
                          ) : (
                            <div className={styles.tableWrap}>
                              <table className={styles.readingsTable}>
                                <thead>
                                  <tr>
                                    <th>Name</th>
                                    <th>Statement Description</th>
                                    <th>Currently</th>
                                    <th>Charge To</th>
                                    <th>Account Charged</th>
                                    <th>Amount (R)</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {unitBillings.map((ub) => {
                                    const currentChargeTo =
                                      normalizeChargeTo(ub);

                                    return (
                                      <tr key={ub.cj_unitbillingid}>
                                        <td>{ub.cj_name || "-"}</td>

                                        <td>
                                          {ub.cj_statementdescription || "-"}
                                        </td>

                                        <td>
                                          <strong>
                                            {currentChargeTo === "tenant"
                                              ? "Tenant"
                                              : "Owner"}
                                          </strong>
                                        </td>

                                        <td>
                                          <select
                                            className={styles.readingsSelect}
                                            value={
                                              chargeToDraft[
                                                ub.cj_unitbillingid
                                              ] || currentChargeTo
                                            }
                                            onChange={(e) =>
                                              updateDraft(
                                                ub.cj_unitbillingid,
                                                e.target.value,
                                              )
                                            }
                                          >
                                            <option value="owner">Owner</option>
                                            <option value="tenant">
                                              Tenant
                                            </option>
                                          </select>
                                        </td>

                                        <td>
                                          {ub[
                                            "_cj_accountcharged_value@OData.Community.Display.V1.FormattedValue"
                                          ] || "-"}
                                        </td>

                                        <td>
                                          {ub.cj_amount !== undefined &&
                                          ub.cj_amount !== null
                                            ? `R ${formatRand(ub.cj_amount)}`
                                            : "-"}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}

                          <div className={styles.modalActions}>
                            <button
                              className={styles.buildingViewBtn}
                              disabled={savingSplit}
                              onClick={handleSaveSplitChanges}
                            >
                              {savingSplit
                                ? "Sending Approval..."
                                : "Send Approval Email"}
                            </button>
                            <button
                              className={styles.modalBtnSecondary}
                              onClick={() => setShowSplitModal(false)}
                            >
                              Close
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {showTenantDetailsModal && (
                  <div
                    className={styles.modalOverlay}
                    onClick={() => {
                      if (!savingSplit) {
                        setShowTenantDetailsModal(false);
                        setTenantErrors({});
                      }
                    }}
                  >
                    <div
                      className={styles.modal}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className={styles.modalHeaderRow}>
                        <h2 className={styles.modalTitle}>Tenant Details</h2>
                        <button
                          className={styles.modalClose}
                          onClick={() => {
                            if (!savingSplit) {
                              setShowTenantDetailsModal(false);
                              setTenantErrors({});
                            }
                          }}
                        >
                          ✕
                        </button>
                      </div>

                      <div className={styles.modalBody}>
                        <p className={styles.infoText}>
                          Because one or more charges were changed to{" "}
                          <strong>Tenant</strong>, please enter the tenant’s
                          details before sending the approval email.
                        </p>

                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>
                            First Name
                          </label>
                          <input
                            type="text"
                            className={styles.inputFieldLight}
                            value={tenantDetails.first_name}
                            onChange={(e) =>
                              handleTenantDetailChange(
                                "first_name",
                                e.target.value,
                              )
                            }
                          />
                          {tenantErrors.first_name && (
                            <div className={styles.errorText}>
                              {tenantErrors.first_name}
                            </div>
                          )}
                        </div>

                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>Last Name</label>
                          <input
                            type="text"
                            className={styles.inputFieldLight}
                            value={tenantDetails.last_name}
                            onChange={(e) =>
                              handleTenantDetailChange(
                                "last_name",
                                e.target.value,
                              )
                            }
                          />
                          {tenantErrors.last_name && (
                            <div className={styles.errorText}>
                              {tenantErrors.last_name}
                            </div>
                          )}
                        </div>

                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>Email</label>
                          <input
                            type="email"
                            className={styles.inputFieldLight}
                            value={tenantDetails.email}
                            onChange={(e) =>
                              handleTenantDetailChange("email", e.target.value)
                            }
                          />
                          {tenantErrors.email && (
                            <div className={styles.errorText}>
                              {tenantErrors.email}
                            </div>
                          )}
                        </div>

                        <div className={styles.inputGroup}>
                          <label className={styles.inputLabel}>
                            Contact Number
                          </label>
                          <input
                            type="tel"
                            className={styles.inputFieldLight}
                            value={tenantDetails.contact_number}
                            onChange={(e) =>
                              handleTenantDetailChange(
                                "contact_number",
                                e.target.value,
                              )
                            }
                          />
                          {tenantErrors.contact_number && (
                            <div className={styles.errorText}>
                              {tenantErrors.contact_number}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className={styles.modalActions}>
                        <button
                          className={styles.modalBtnPrimary}
                          disabled={savingSplit}
                          onClick={handleTenantDetailsSubmit}
                        >
                          {savingSplit ? "Submitting..." : "Submit"}
                        </button>

                        <button
                          className={styles.modalBtnSecondary}
                          disabled={savingSplit}
                          onClick={() => {
                            setShowTenantDetailsModal(false);
                            setTenantErrors({});
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showEmailSentModal && (
                  <div
                    className={styles.modalOverlay}
                    onClick={() => setShowEmailSentModal(false)}
                  >
                    <div
                      className={styles.modal}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h2 className={styles.modalTitle}>
                        Confirmation Email Sent
                      </h2>
                      <div className={styles.modalBody}>
                        <p>
                          We’ve sent a confirmation email to{" "}
                          <strong>{getUserEmail()}</strong>. You have 30 minutes
                          to respond before the link expires.
                        </p>
                      </div>
                      <div className={styles.modalActions}>
                        <button
                          className={styles.modalBtnPrimary}
                          onClick={() => setShowEmailSentModal(false)}
                        >
                          OK
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {showToast && (
                  <div className={styles.toastBox}>{toastText}</div>
                )}
              </>
            ) : (
              <p className={styles.errorText}>No levy account details found.</p>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default LevyAccountDetailPage;
